import { Button, EmptyState, PageHeader, Skeleton, Stack, Surface, Text } from "@app/ui"
import { RefId, StatCard, type StatCardProps, type StatTrend, Tag } from "@app/ui/next"
import { useQuery } from "@tanstack/react-query"
import { Link, createRoute } from "@tanstack/react-router"
import type { CSSProperties, ReactNode } from "react"
import { type ActivityEvent, activityQueryKeys, listRecentActivity } from "../api/activity"
import { type DashboardStats, dashboardQueryKeys, getDashboardStats } from "../api/dashboard"
import { ThemeToggle } from "../components/ThemeToggle"
import { humanizeStatus } from "../lib/format"
import { useScope } from "../scope/scope-context"
import { useScopeLabels } from "../scope/use-scope-labels"
import { Route as RootRoute } from "./__root"
import type { DashboardMetric } from "./dashboard-tones"

/** /next StatCard icon-chip tone (not re-exported by name, so derive it). */
type StatTone = NonNullable<StatCardProps["tone"]>

export const Route = createRoute({
	getParentRoute: () => RootRoute,
	path: "/",
	component: IndexPage,
})

const COMPANY_LINE = "RAM-OS — shipment + document operations."

const CARD_LINK_STYLE: CSSProperties = {
	textDecoration: "none",
	color: "inherit",
	display: "block",
}
// Same as CARD_LINK_STYLE but positioned so the "all scopes" caption can pin
// to the notifications tile's corner.
const NOTIF_CARD_STYLE: CSSProperties = { ...CARD_LINK_STYLE, position: "relative" }

// "LIVE" status row above the page header — a pulsing jade dot, an overline
// caption, and the theme toggle. Route-level layout, so inline-styled (the
// design export does the same).
const LIVE_ROW_STYLE: CSSProperties = {
	display: "flex",
	alignItems: "center",
	justifyContent: "space-between",
	gap: 16,
}
const LIVE_LEAD_STYLE: CSSProperties = {
	display: "flex",
	alignItems: "center",
	gap: 10,
	minWidth: 0,
}
const LIVE_DOT_STYLE: CSSProperties = {
	width: 8,
	height: 8,
	flex: "none",
	borderRadius: "50%",
	background: "var(--gm-color-brand-9)",
	boxShadow: "0 0 0 4px color-mix(in srgb, var(--gm-color-brand-9) 22%, transparent)",
}
const ACTIVITY_SURFACE_STYLE: CSSProperties = { padding: 0, overflow: "hidden" }

// The notifications tile carries a subtle "all scopes" caption (it stays
// global server-side even when a scope is active) — relative positioning on the
// card Link lets the caption pin to the tile's top-right without disturbing the
// StatCard layout.
const NOTIF_BADGE_STYLE: CSSProperties = {
	position: "absolute",
	top: 10,
	right: 12,
	pointerEvents: "none",
}

// Compact inline glyphs (stroke-based, currentColor) so each tile carries a
// metric icon without pulling in an icon dependency — same pattern as AppNav.
const glyph = (path: ReactNode): ReactNode => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		{path}
	</svg>
)

const ICON_SHIPMENTS = glyph(
	<>
		<path d="M3 7h13v10H3z" />
		<path d="M16 10h4l1 3v4h-5z" />
		<circle cx="7" cy="18" r="1.6" />
		<circle cx="17.5" cy="18" r="1.6" />
	</>,
)
const ICON_DOCS = glyph(
	<>
		<path d="M6 2h8l4 4v16H6z" />
		<path d="M14 2v4h4" />
		<path d="M9 13h6M9 17h6" />
	</>,
)
const ICON_ALERT = glyph(
	<>
		<path d="M12 3l9 16H3z" />
		<path d="M12 10v4M12 17h.01" />
	</>,
)
const ICON_BELL = glyph(
	<>
		<path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
		<path d="M10 20a2 2 0 0 0 4 0" />
	</>,
)
const ICON_EDIT = glyph(
	<>
		<path d="M4 20h4L18 10l-4-4L4 16z" />
		<path d="M13.5 6.5l4 4" />
	</>,
)

interface MetricSpec {
	readonly metric: DashboardMetric
	readonly label: string
	readonly icon: ReactNode
	readonly to: string
	/** Fixed icon-chip accent — one per metric (not value-derived). */
	readonly tone: StatTone
	readonly value: (s: DashboardStats) => number
	/** Sub-value line under the headline (severity- or freshness-aware). */
	readonly caption: (s: DashboardStats) => string
	/** Optional signed delta chip (only the active-shipments tile carries one). */
	readonly delta?: (s: DashboardStats) => string
	readonly trend?: (s: DashboardStats) => StatTrend
}

const METRICS: readonly MetricSpec[] = [
	{
		metric: "active",
		label: "Active shipments",
		icon: ICON_SHIPMENTS,
		to: "/shipments",
		tone: "brand",
		value: (s) => s.activeShipments,
		caption: () => "new vs last week",
		delta: (s) => `${s.shipmentsDelta >= 0 ? "+" : ""}${s.shipmentsDelta}`,
		trend: (s) => (s.shipmentsDelta > 0 ? "up" : s.shipmentsDelta < 0 ? "down" : "flat"),
	},
	{
		metric: "overdue",
		label: "Overdue documents",
		icon: ICON_DOCS,
		to: "/shipments",
		tone: "warning",
		value: (s) => s.overdueDocuments,
		caption: (s) => (s.overdueDocuments > 0 ? `oldest ${s.overdueOldestDays} days` : "all current"),
	},
	{
		metric: "heists",
		label: "Heists pending review",
		icon: ICON_ALERT,
		// Was "/shipments", which dropped you on an unfiltered shipment list
		// with no indication of which rows were flagged. `/heists` defaults to
		// the PENDING_REVIEW filter, so the count you clicked is the list you
		// land on.
		to: "/heists",
		tone: "danger",
		value: (s) => s.pendingHeists,
		caption: (s) => (s.pendingHeists > 0 ? "under investigation" : "none open"),
	},
	{
		metric: "notifications",
		label: "Unread notifications",
		icon: ICON_BELL,
		to: "/notifications",
		tone: "neutral",
		value: (s) => s.unreadNotifications,
		caption: (s) => `${s.notificationsToday} today`,
	},
]

function IndexPage() {
	const { isGlobal } = useScope()
	const { countryLabel, verticalLabel } = useScopeLabels()
	// Global default keeps the brand framing; a scope (incl. half-scope) shows
	// the resolved dimension names from useScopeLabels.
	const liveText = isGlobal
		? "Live — Ajman Free Zone · cashew & soybean exports"
		: `Live — ${countryLabel} · ${verticalLabel}`
	return (
		<Stack gap="6" align="stretch">
			<div style={LIVE_ROW_STYLE}>
				<div style={LIVE_LEAD_STYLE}>
					<span aria-hidden="true" style={LIVE_DOT_STYLE} />
					<Text variant="overline" tone="muted">
						{liveText}
					</Text>
				</div>
				<ThemeToggle />
			</div>
			<PageHeader title="Dashboard" subtitle={COMPANY_LINE} actions={<HeaderActions />} />
			<StatsSection />
			<ActivitySection />
		</Stack>
	)
}

// Presentational page actions mirroring the export. Behaviour (export job,
// new-shipment flow) is wired in a later phase — these are intentionally inert.
function HeaderActions() {
	return (
		<Stack direction="row" gap="2">
			<Button variant="secondary" size="sm">
				Export report
			</Button>
			<Button variant="primary" size="sm">
				New shipment
			</Button>
		</Stack>
	)
}

function StatsSection() {
	const { params, isGlobal, clear } = useScope()
	const { countryLabel, verticalLabel } = useScopeLabels()
	const query = useQuery({
		queryKey: dashboardQueryKeys.stats(params),
		queryFn: ({ signal }) => getDashboardStats(params, signal),
	})

	if (query.isPending) {
		return (
			<div className="gm-dashboard__grid" aria-busy="true">
				{METRICS.map((m) => (
					<Surface key={m.metric} elevation="1" tone="raised" bordered>
						<Stack gap="3" align="start">
							<Skeleton width={32} height={32} radius={8} />
							<Skeleton width="60%" height={12} />
							<Skeleton width="40%" height={28} />
						</Stack>
					</Surface>
				))}
			</div>
		)
	}

	if (query.isError) {
		return (
			<Surface elevation="0" tone="muted" bordered>
				<Stack gap="2" align="start">
					<Text variant="body-sm" tone="danger">
						Couldn't load dashboard stats.
					</Text>
					<Button variant="secondary" size="sm" onClick={() => query.refetch()}>
						Retry
					</Button>
				</Stack>
			</Surface>
		)
	}

	const stats = query.data
	// Notifications stay global server-side (decision 4) — exclude them from the
	// "is this scope empty?" test so a scoped-but-quiet view is recognised.
	const scopedEmpty =
		!isGlobal &&
		stats.activeShipments === 0 &&
		stats.overdueDocuments === 0 &&
		stats.pendingHeists === 0

	return (
		<Stack gap="4" align="stretch">
			{scopedEmpty ? (
				<EmptyState
					title={`No activity in ${countryLabel} · ${verticalLabel} yet`}
					description="No active shipments, overdue documents, or heists match this scope. Try a broader filter."
					action={
						<Button variant="secondary" size="sm" onClick={() => clear()}>
							Clear filters
						</Button>
					}
				/>
			) : null}
			<div className="gm-dashboard__grid">
				{METRICS.map((m) => {
					// The unread count never re-scopes; when a scope is active, mark the
					// card with a subtle "all scopes" caption so the global figure doesn't
					// read as scoped. Badge sits inside the Link so each tile stays a bare
					// grid item (no wrapper divs).
					const flagged = m.metric === "notifications" && !isGlobal
					// exactOptionalPropertyTypes: only spread delta/trend when the metric
					// actually carries one (active shipments), never as `undefined`.
					const deltaProps =
						m.delta && m.trend ? { delta: m.delta(stats), trend: m.trend(stats) } : {}
					return (
						<Link key={m.metric} to={m.to} style={flagged ? NOTIF_CARD_STYLE : CARD_LINK_STYLE}>
							<StatCard
								label={m.label}
								value={m.value(stats)}
								icon={m.icon}
								tone={m.tone}
								caption={m.caption(stats)}
								{...deltaProps}
							/>
							{flagged ? (
								<Text variant="caption" tone="subtle" style={NOTIF_BADGE_STYLE}>
									all scopes
								</Text>
							) : null}
						</Link>
					)
				})}
			</div>
		</Stack>
	)
}

const FEED_HEAD_RIGHT_STYLE: CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	gap: 14,
}
const FEED_VIEWALL_STYLE: CSSProperties = {
	fontSize: 13,
	fontWeight: 500,
	color: "var(--gm-color-accent-fg)",
	textDecoration: "none",
}

function ActivitySection() {
	const { params } = useScope()
	const query = useQuery({
		queryKey: activityQueryKeys.recent(params),
		queryFn: ({ signal }) => listRecentActivity(params, signal),
	})

	return (
		<Surface elevation="1" tone="raised" bordered style={ACTIVITY_SURFACE_STYLE}>
			<div className="gm-feed__head">
				<Text variant="heading-sm">Recent activity</Text>
				<span style={FEED_HEAD_RIGHT_STYLE}>
					<span className="gm-feed__head-meta">Last 7 days</span>
					<Link to="/activity" style={FEED_VIEWALL_STYLE}>
						View all →
					</Link>
				</span>
			</div>
			<ActivityBody query={query} />
		</Surface>
	)
}

function ActivityBody({
	query,
}: {
	query: ReturnType<typeof useQuery<ActivityEvent[]>>
}) {
	if (query.isPending) {
		return (
			<div className="gm-feed__pad">
				<Stack gap="3" align="stretch" aria-busy="true">
					{[0, 1, 2].map((i) => (
						<Stack key={i} gap="1" align="start">
							<Skeleton width="50%" height={14} />
							<Skeleton width="30%" height={12} />
						</Stack>
					))}
				</Stack>
			</div>
		)
	}

	if (query.isError) {
		return (
			<div className="gm-feed__pad">
				<Stack gap="2" align="start">
					<Text variant="body-sm" tone="danger">
						Couldn't load recent activity.
					</Text>
					<Button variant="secondary" size="sm" onClick={() => query.refetch()}>
						Retry
					</Button>
				</Stack>
			</div>
		)
	}

	const events = query.data
	if (events.length === 0) {
		return (
			<div className="gm-feed__pad">
				<EmptyState
					title="No recent activity"
					description="Document changes, correction rounds, and alerts from the last 7 days will show up here."
				/>
			</div>
		)
	}

	return (
		<ul className="gm-feed">
			{events.map((event) => (
				<ActivityRow key={event.id} event={event} />
			))}
		</ul>
	)
}

// --- feed shaping helpers ------------------------------------------------
// The engine emits titles/subtitles that embed raw UUIDs (e.g. "Document
// <uuid> created", "PENDING · shipment <uuid>"). We strip those into short
// mono ref chips so the feed reads as a human log, not an id dump.
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i

const lastSegment = (id: string): string => `#${id.split("-").pop() ?? id}`

/** Split a title into its human text + an optional trailing ref chip. */
const shapeTitle = (raw: string): { text: string; chip: string | null } => {
	const match = raw.match(UUID_RE)
	if (!match) return { text: raw, chip: null }
	const uuid = match[0]
	const text = raw
		.replace(uuid, "")
		.replace(/\s{2,}/g, " ")
		.trim()
	return { text, chip: lastSegment(uuid) }
}

/** Parse "STATUS · noun <uuid>" subtitles into a status pill + a ref chip. */
const shapeMeta = (
	subtitle: string | null,
): { status: string | null; ref: string | null; text: string | null } => {
	if (!subtitle) return { status: null, ref: null, text: null }
	const first = subtitle.split(" · ")[0]?.trim() ?? ""
	const status = /^[A-Z][A-Z_]+$/.test(first) ? first : null
	const uuid = subtitle.match(UUID_RE)
	const ref = uuid ? lastSegment(uuid[0]) : null
	const text = !status && !ref ? subtitle : null
	return { status, ref, text }
}

// The subtitle tokens surfaced here (document-review status, correction-round
// layer, heist classification) are categorical labels — NOT one of the five
// shipment logistics statuses — so they render as a neutral RAM-OS `Tag`, never
// a `StatusBadge` (which is reserved for shipment statuses).

// Event kind → tinted icon. Heist alerts read danger; corrections warning;
// notifications info; document events stay neutral.
const KIND_VISUAL: Record<ActivityEvent["kind"], { icon: ReactNode; tone: string }> = {
	document_created: { icon: ICON_DOCS, tone: "" },
	correction_round_opened: { icon: ICON_EDIT, tone: "warning" },
	merchant_change_flagged: { icon: ICON_ALERT, tone: "danger" },
	notification_dispatched: { icon: ICON_BELL, tone: "info" },
	onboarding_completed: { icon: ICON_EDIT, tone: "" },
	shipment_status_changed: { icon: ICON_SHIPMENTS, tone: "info" },
	container_added: { icon: ICON_SHIPMENTS, tone: "" },
}

const relativeTime = (iso: string): string => {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return ""
	const sec = Math.round((Date.now() - date.getTime()) / 1000)
	if (sec < 45) return "just now"
	const min = Math.round(sec / 60)
	if (min < 60) return `${min}m ago`
	const hr = Math.round(min / 60)
	if (hr < 24) return `${hr}h ago`
	const day = Math.round(hr / 24)
	if (day < 7) return `${day}d ago`
	return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function ActivityRow({ event }: { event: ActivityEvent }) {
	const visual = KIND_VISUAL[event.kind]
	const { text, chip } = shapeTitle(event.title)
	const { status, ref, text: metaText } = shapeMeta(event.subtitle)
	const iconClass = visual.tone ? `gm-feed__icon gm-feed__icon--${visual.tone}` : "gm-feed__icon"
	const titleInner = event.link ? <Link to={event.link}>{text}</Link> : text
	// A document/entity ref may arrive embedded in either the title or the
	// subtitle; surface whichever we found as the row's mono ref chip.
	const refChip = chip ?? ref
	return (
		<li className="gm-feed__row">
			<span aria-hidden="true" className={iconClass}>
				{visual.icon}
			</span>
			<div className="gm-feed__main">
				<span className="gm-feed__title">{titleInner}</span>
				{metaText ? <span className="gm-feed__subtitle">{metaText}</span> : null}
			</div>
			{refChip ? (
				<RefId className="gm-feed__ref" plain value={refChip} />
			) : (
				<span aria-hidden="true" />
			)}
			{status ? (
				<span className="gm-feed__status">
					<Tag>{humanizeStatus(status)}</Tag>
				</span>
			) : (
				<span aria-hidden="true" />
			)}
			<span className="gm-feed__time">{relativeTime(event.createdAt)}</span>
		</li>
	)
}

export { IndexPage }
